// Quem o limitador de login barrou.
//
// POR QUE ESTA TELA EXISTE: o limitador sempre viveu em memória. Isso
// bastava para PROTEGER e não bastava para OPERAR — quando o cliente ligava
// dizendo "não consigo entrar", não havia onde olhar.
//
// E o caso é mais confuso do que parece: depois de cinco erros, a SEXTA
// tentativa com a senha CERTA também é barrada. A pessoa lembra a senha,
// digita certo, e continua de fora. Sem esta tela, nem ela nem o suporte
// têm como saber o porquê.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { bloqueios, type BloqueioLogin } from "../api/admin";
import { explicar } from "../api/problema";
import { Botao, Cabecalho, Celula, Estado, Linha, Selo, Tabela } from "./comuns";

/** Quanto falta, em palavras. "em 7 minutos" é mais útil que um horário. */
function faltam(ate: string): string {
  const ms = new Date(ate).getTime() - Date.now();
  if (ms <= 0) return "destravando";
  const min = Math.ceil(ms / 60000);
  return min <= 1 ? "menos de 1 minuto" : `${min} minutos`;
}

function quando(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

export function Bloqueios() {
  const fila = useQueryClient();
  const [historico, setHistorico] = useState(false);
  const [erro, setErro] = useState("");

  const lista = useQuery({
    queryKey: ["p", "bloqueios", historico],
    queryFn: () => bloqueios.listar(historico ? {} : { ativos: "1" }),
    // Bloqueio expira sozinho em 15 minutos. Sem recarregar, a tela mostra
    // "bloqueado" por meia hora depois de ter destravado — e aí alguém
    // clica em liberar algo que já estava livre.
    refetchInterval: 30_000,
  });

  const liberar = useMutation({
    mutationFn: (b: BloqueioLogin) => bloqueios.liberar(b.uuid),
    onSuccess: () => {
      setErro("");
      void fila.invalidateQueries({ queryKey: ["p", "bloqueios"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const itens = lista.data ?? [];
  const ativos = itens.filter((b) => b.ativo);

  return (
    <>
      <Cabecalho
        titulo="Bloqueios de login"
        descricao={
          "Cinco senhas erradas na mesma caixa, ou trinta do mesmo IP, barram " +
          "por 15 minutos. Enquanto vale, a senha certa também é barrada."
        }
        acao={
          <Botao
            tom="neutro"
            aoClicar={() => setHistorico((h) => !h)}
            icone={historico ? "ShieldAlert" : "ScrollText"}
          >
            {historico ? "Ver só os ativos" : "Ver histórico"}
          </Botao>
        }
      />

      {erro && (
        <p className="mb-4 text-sm text-perigo" role="alert">
          {erro}
        </p>
      )}

      <Estado consulta={lista} vazio={itens.length === 0}>
        <>
          {!historico && ativos.length > 0 && (
            <p className="mb-3 text-sm text-tinta-fraca">
              {ativos.length === 1
                ? "1 bloqueio ativo agora."
                : `${ativos.length} bloqueios ativos agora.`}
            </p>
          )}
          <Tabela
            colunas={["Quem", "Tipo", "Domínio", "IP de origem", "Começou", "Situação", ""]}
          >
            {itens.map((b) => (
              <Linha key={b.uuid}>
                <Celula>
                  <span className="font-medium text-tinta">{b.alvo}</span>
                </Celula>
                <Celula>
                  {/* OS DOIS TIPOS PRECISAM SER DISTINGUÍVEIS NA HORA.
                      Liberar a conta não adianta quando quem está barrado é
                      o IP — e esse engano custa um chamado inteiro. */}
                  <Selo
                    tom={b.tipo === "ip" ? "aviso" : "neutro"}
                    texto={b.tipo === "ip" ? "IP inteiro" : "conta"}
                  />
                </Celula>
                <Celula>{b.dominio || "—"}</Celula>
                <Celula>{b.ip || "—"}</Celula>
                <Celula>{quando(b.criado_em)}</Celula>
                <Celula>
                  {b.ativo ? (
                    <span className="text-perigo">
                      bloqueado — destrava em {faltam(b.expira_em)}
                    </span>
                  ) : b.liberado_em ? (
                    <span className="text-tinta-fraca">
                      liberado por {b.liberado_por || "alguém"}
                    </span>
                  ) : (
                    <span className="text-tinta-fraca">expirou sozinho</span>
                  )}
                </Celula>
                <Celula>
                  {b.ativo && (
                    <button
                      onClick={() => liberar.mutate(b)}
                      className="text-xs text-acao underline"
                    >
                      liberar agora
                    </button>
                  )}
                </Celula>
              </Linha>
            ))}
          </Tabela>
        </>
      </Estado>

      {/* O OUTLOOK NÃO PASSA POR AQUI, e quem não souber disso vai procurar
          nesta tela um bloqueio que nunca vai aparecer. */}
      <p className="mt-6 text-xs text-tinta-fraca">
        Esta tela mostra o login do <strong>webmail</strong>. Quem erra a senha no
        Outlook ou no celular bate noutra proteção, no servidor de e-mail, que
        atrasa a resposta em vez de bloquear — e não aparece aqui.
      </p>
    </>
  );
}
